import { useState } from "react";
import type { DailyEntryResponse } from "@consistency-circle/shared";

import { computeDayScore, tierLabels, type DayScoreTier } from "../../../lib/day-score";

const SIZE = 104;
const STROKE = 10;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const tierStroke: Record<DayScoreTier, string> = {
  optimal: "stroke-green-600 dark:stroke-green-400",
  good: "stroke-primary",
  fair: "stroke-amber-600 dark:stroke-amber-400",
  attention: "stroke-destructive",
};

const tierText: Record<DayScoreTier, string> = {
  optimal: "text-green-600 dark:text-green-400",
  good: "text-primary",
  fair: "text-amber-600 dark:text-amber-400",
  attention: "text-destructive",
};

export function DayScoreRing({
  goalCompletionPercent,
  entry,
}: {
  goalCompletionPercent: number | null;
  entry: DailyEntryResponse | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const { score, tier, factors } = computeDayScore(goalCompletionPercent, entry);

  const progress = score === null ? 0 : score / 100;

  return (
    <button
      type="button"
      onClick={() => setExpanded((open) => !open)}
      aria-label="Day score, tap for the breakdown"
      aria-expanded={expanded}
      className="w-full text-left rounded-2xl border border-border bg-card p-4 space-y-3 transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <div className="flex items-center gap-5">
        <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
          <svg width={SIZE} height={SIZE} className="-rotate-90 absolute inset-0">
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              className="stroke-muted-foreground/15"
            />
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={CIRCUMFERENCE * (1 - progress)}
              className={`transition-[stroke-dashoffset] duration-500 ${tier ? tierStroke[tier] : "stroke-muted-foreground/30"}`}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold tabular-nums">{score ?? "–"}</span>
          </div>
        </div>

        <div className="min-w-0 space-y-0.5">
          <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
            Day score
          </p>
          <p className={`text-lg font-semibold ${tier ? tierText[tier] : "text-muted-foreground"}`}>
            {tier ? tierLabels[tier] : "No data yet"}
          </p>
          <p className="text-xs text-muted-foreground">
            {score === null
              ? "Complete a goal or log your day to start scoring."
              : expanded
                ? "Blend of goals, mood, sleep, and lifestyle."
                : "Tap to see what's contributing."}
          </p>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border pt-3 space-y-2">
          {factors.map((factor) => (
            <div key={factor.key} className="flex items-center gap-3">
              <span className="w-20 shrink-0 text-sm font-medium">{factor.label}</span>
              <span className="flex-1 min-w-0 truncate text-xs text-muted-foreground">
                {factor.detail}
              </span>
              <span
                className={`w-7 shrink-0 text-right text-sm font-semibold tabular-nums ${
                  factor.score === null ? "text-muted-foreground/50" : ""
                }`}
              >
                {factor.score === null ? "–" : Math.round(factor.score)}
              </span>
            </div>
          ))}
        </div>
      )}
    </button>
  );
}
